import { CreateShapeResponse, EditShapeResponse } from '@ais-ui/api';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  LoadingSpinner,
  SelectType,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';
import { Group, useGetShareGroups } from '@ais-ui/websockets';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { GroupSelect } from '../GroupSelect';
import { GroupMultiSelect } from '../../Groups/GroupMultiSelect';
import { grabShapeInitialGroups } from '../../Groups/grabInitialGroups';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { mutateCallbacks } from '../../utils/mutateCallbacks';
import { useEffect, useMemo, useState } from 'react';
import { UseMutateFunction } from '@tanstack/react-query';
import { useLocalization } from '@ais-ui/locale-management';

interface SaveShapeGroupCreateDialogProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  mutate: UseMutateFunction<CreateShapeResponse, unknown, GeoJson, unknown>;
  action: 'create';
  shape?: GeoJson;
}

interface SaveShapeGroupEditDialogProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  mutate: UseMutateFunction<
    EditShapeResponse,
    unknown,
    { id: string; json: GeoJson },
    unknown
  >;
  action: 'edit';
  shape: GeoJson;
}

type SaveShapeGroupDialogProps =
  | SaveShapeGroupCreateDialogProps
  | SaveShapeGroupEditDialogProps;

const schema = z.object({
  name: z.string().nonempty({ message: 'Name is required' }),
  parentId: z.string().optional(),
  selectedGroups: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
      })
    )
    .optional(),
});

interface ShapeGroupFormData {
  name: string;
  parentId: string;
  selectedGroups: SelectType[];
}

interface ShapeGroupFormProps {
  onSubmit: (data: ShapeGroupFormData) => void;
  isSubmitting: boolean;
  shape?: GeoJson;
  groups: Group[];
}

const SaveShapeGroupForm: React.FC<ShapeGroupFormProps> = ({
  onSubmit,
  isSubmitting,
  shape,
  groups,
}) => {
  const { messages } = useLocalization();
  const form = useForm<ShapeGroupFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: shape?.properties.name || '',
      parentId: shape?.properties.parentId || '',
      selectedGroups: grabShapeInitialGroups(shape, groups),
    },
  });

  const parentBlacklist = useMemo(() => {
    if (shape?.properties?.id) {
      return [shape.properties.id];
    } else {
      return [];
    }
  }, [shape]);

  const parentId = form.watch('parentId');

  useEffect(() => {
    if (parentId) {
      form.setValue('selectedGroups', []);
    }
  }, [parentId, form]);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div className="grid grid-cols-[auto,auto,1fr] gap-y-4 gap-x-2 items-center">
        <div></div>
        <Label htmlFor="shape-name">
          {messages.shapesCreateShapeGroupNameLabel} *
        </Label>
        <div>
          <Controller
            name="name"
            control={form.control}
            render={({ field }) => <Input id="shape-name" {...field} />}
          />
          {form.formState.errors.name && (
            <p className="text-red-500 text-sm">
              {form.formState.errors.name.message}
            </p>
          )}
        </div>
        <div></div>
        <Label htmlFor="shape-parent-group">
          {messages.shapesCreateShapeGroupParentGroupLabel}
        </Label>
        <div>
          <Controller
            name="parentId"
            control={form.control}
            render={({ field }) => (
              <GroupSelect
                value={field.value}
                onChange={field.onChange}
                blacklist={parentBlacklist}
              />
            )}
          />
          {form.formState.errors.parentId && (
            <p className="text-red-500 text-sm">
              {form.formState.errors.parentId.message}
            </p>
          )}
        </div>
        <Tooltip>
          <TooltipTrigger type="button" className="self-center">
            <IoMdInformationCircleOutline className="text-primary" />
          </TooltipTrigger>
          <TooltipContent side="right">
            {messages.shapesCreateShapeGroupShareTooltip}
          </TooltipContent>
        </Tooltip>
        <Label htmlFor="shape-groups">
          {messages.shapesCreateShapeGroupShareLabel}
        </Label>
        <div>
          <Controller
            name="selectedGroups"
            control={form.control}
            render={({ field }) => (
              <GroupMultiSelect
                {...field}
                disabled={!!parentId}
                disabledReason={messages.shapeDisabledShareText}
              />
            )}
          />
          {form.formState.errors.selectedGroups && (
            <p className="text-red-500 text-sm">
              {form.formState.errors.selectedGroups.message}
            </p>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button
          className="mt-4"
          id="shape-confirm-save-button"
          disabled={isSubmitting}
        >
          {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
        </Button>
      </DialogFooter>
    </form>
  );
};

export function SaveShapeGroupDialog(props: SaveShapeGroupDialogProps) {
  const { data: groups, isLoading: isLoadingGroups } = useGetShareGroups();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { messages } = useLocalization();

  const { toast } = useToast();

  const dialogTitle =
    props.action === 'edit'
      ? messages.shapesEditShapeGroupTitle
      : messages.shapesCreateShapeGroupTitle;
  let dialogDescription;

  if (props.action === 'edit') {
    dialogDescription = messages.shapesEditShapeGroupDescription;
  } else {
    dialogDescription = messages.shapesCreateShapeGroupDescription;
  }

  const onSubmit = (data: ShapeGroupFormData) => {
    const geoJson: GeoJson = {
      type: 'FeatureCollection',
      properties: {
        name: data.name,
        parentId: data.parentId,
        acl: data.selectedGroups.map((group) => group.value),
      },
      features: props.shape?.features ?? [],
    };

    const callBacks = mutateCallbacks(
      toast,
      messages,
      props.action,
      'group',
      setIsSubmitting,
      props.setShowDialog
    );

    if (props.action === 'edit') {
      const shapeGroupName = props.shape.properties.name
        ? props.shape.properties.name
        : 'unknown shape group';
      const id = props.shape?.properties.id;
      if (!id) {
        toast({
          className: 'save-shape-group-error-toast',
          title: 'Error!',
          description: messages.shapesEditGroupFailed.replace(
            '{label}',
            shapeGroupName
          ),
          variant: 'destructive',
        });
        return;
      }
      props.mutate({ id, json: geoJson }, callBacks);
    } else {
      props.mutate(geoJson, callBacks);
    }
  };

  return (
    <Dialog open={props.showDialog} onOpenChange={props.setShowDialog}>
      <DialogContent id="mutate-shape-group-dialog">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>
        {isLoadingGroups && <LoadingSpinner></LoadingSpinner>}
        {!isLoadingGroups && groups && (
          <SaveShapeGroupForm
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            shape={props.shape}
            groups={groups}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
